import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/core/api/client";
import type { MoveResult, RunLog } from "@/games/registry";

export interface RunView {
  id: number;
  game_key: string;
  status: "in_progress" | "finished" | "rejected" | "abandoned";
  day: string | null;
  deal_number: number | null;
  is_ranked: boolean;
  started_at: string;
  finished_at: string | null;
  score: number | null;
  duration_ms: number | null;
  stats: Record<string, number>;
  rejected_reason: string | null;
  rank: number | null;
  server_now: string;
}

/**
 * Only the start of a run carries its seed: it deals the whole game (ADR 0075). A game whose
 * deal hides something gets its opening view instead, and plays move by move (ADR 0078).
 */
export interface StartedRun extends RunView {
  seed?: number;
  view?: unknown;
}

export interface BoardEntry {
  rank: number;
  participant: string;
  score: number;
  duration_ms: number;
  finished_at: string;
  is_me: boolean;
}

export interface GameSummary {
  key: string;
  version: number;
  clock_hz: number | null;
  players_today: number;
  best_today: BoardEntry | null;
  my_ranked_run: RunView | null;
  personal_best: { score: number; duration_ms: number; finished_at: string } | null;
}

export interface GamesHub {
  day: string;
  deal_number: number;
  games: GameSummary[];
}

export interface GameBoard {
  day: string;
  deal_number: number;
  players: number;
  entries: BoardEntry[];
}

export function useGames({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["games"],
    queryFn: () => api<GamesHub>("/api/games/"),
    enabled,
  });
}

export function useGameBoard(key: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["games", key, "board"],
    queryFn: () => api<GameBoard>(`/api/games/${key}/board/`),
    enabled,
    // Other people's runs land while this one is played; a board is cheap to read.
    refetchInterval: 30_000,
  });
}

export function useStartRun(key: string) {
  return useMutation({
    mutationFn: (mode: "daily" | "free") =>
      api<StartedRun>(`/api/games/${key}/runs/`, { method: "POST", json: { mode } }),
  });
}

export function useFinishRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, run }: { id: number; run: RunLog }) =>
      api<RunView>(`/api/games/runs/${id}/finish/`, { method: "POST", json: run }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["games"] }),
  });
}

/** One move of a hidden-information game, played by the server (ADR 0078). */
export function playMove(id: number, move: string): Promise<MoveResult> {
  return api<MoveResult>(`/api/games/runs/${id}/move/`, { method: "POST", json: { move } });
}

/**
 * Sends a run as the page goes away: closing the tab ends the run, it does not lose it.
 * `keepalive` lets the request outlive the page; its body is capped at 64 KB, which a
 * half-hour log stays well under.
 */
export function finishRunOnLeave(id: number, run: RunLog): void {
  void api(`/api/games/runs/${id}/finish/`, { method: "POST", json: run, keepalive: true }).catch(
    () => undefined,
  );
}
